import { createAsyncThunk } from "@reduxjs/toolkit";
import { captureException } from "@sentry/browser";

import * as basketAPI from "../../../api/basket";
import * as wishlistsAPI from "../../../api/wishlists";
import { IBasket } from "../../../models/catalogue";
import { FinancingPlan } from "../../../models/financing";
import { LIST_PREVIOUSLY_IN_BASKET } from "../constants";
import { filterFinancingPlans, sortFinancingPlans } from "../utils";

/**
 * Return type for loadBasket thunk.
 */
interface LoadBasketResult {
    basket: IBasket | null;
    previousItemsList: ReturnType<
        typeof wishlistsAPI.listLines
    > extends Promise<infer T>
        ? T
        : never;
    financingPlans: FinancingPlan[];
}

/**
 * Load the basket, with the financing plans it is eligible for, and the
 * previously-in-basket wishlist lines.
 *
 * The plans come inside the basket response, chosen by the same offers that
 * priced the basket, so reloading the basket is what refreshes them.
 *
 * @sideEffects
 * - Updates `data.basket` with fetched basket data via extraReducers
 * - Updates `data.wishlists[LIST_PREVIOUSLY_IN_BASKET]` with wishlist items via extraReducers
 * - Updates `data.financing_plans` with filtered and sorted plans via extraReducers
 * - Logs to console in debug mode
 */
export const loadBasket = createAsyncThunk(
    "checkout/loadBasket",
    async (_): Promise<LoadBasketResult> => {
        // Load the basket
        const loadingBasket = basketAPI.load();
        // Load wishlist lines, but allow this to fail gracefully
        const loadingPrevious = wishlistsAPI
            .listLines(LIST_PREVIOUSLY_IN_BASKET)
            .catch((err) => {
                captureException(err);
                return [];
            });

        console.debug("Loading basket...");
        const [basket, previousItemsList] = await Promise.all([
            loadingBasket,
            loadingPrevious,
        ]);
        console.debug("Loaded basket.");

        return {
            basket,
            previousItemsList,
            financingPlans: sortFinancingPlans(
                filterFinancingPlans(basket?.financing_plans ?? []),
            ),
        };
    },
);
